/**
 * BUSINESS LOGIC (BL): PERSONAL NOISEMETER (GRITO MEXICANO)
 * Capa de lógica de negocio, validaciones, utilidades de formateo y orquestación para Grito Mexicano.
 */

import {
    GetAdminConfig as DLGetAdminConfig,
    UpdateAdminConfig as DLUpdateAdminConfig,
    GetAdminParticipants as DLGetAdminParticipants,
    ResetParticipant as DLResetParticipant,
    DisqualifyParticipant as DLDisqualifyParticipant,
    ExportParticipantsCsv as DLExportParticipantsCsv,
    GetPublicConfig as DLGetPublicConfig,
    RegisterParticipant as DLRegisterParticipant,
    SubmitScore as DLSubmitScore,
    GetRanking as DLGetRanking,
    GetParticipantStatus as DLGetParticipantStatus,
    TrackMetric as DLTrackMetric,
    GetAdminStats as DLGetAdminStats,
    ToggleActivePersonalNoisemeter as DLToggleActivePersonalNoisemeter,
    ResetPersonalNoisemeterData as DLResetPersonalNoisemeterData
} from '../DL/PersonalNoisemeter';

/**
 * Formatea una fecha ISO o timestamp a formato legible de México.
 * @param {string|Date} dateString 
 * @returns {string} Fecha formateada en es-MX (ej. '14/09/2026, 12:30:00 hrs')
 */
export const formatMexicoDate = (dateString) => {
    if (!dateString) return 'No disponible';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Fecha inválida';
        return date.toLocaleString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }) + ' hrs';
    } catch {
        return String(dateString);
    }
};

/**
 * Enmascara o formatea el nombre de un participante según la directriz de privacidad.
 * @param {string} fullName 
 * @param {boolean} mask Si es true: 'Juan Pérez' -> 'Juan P.', 'Juan Carlos Pérez' -> 'Juan P.'
 * @returns {string}
 */
export const formatDisplayName = (fullName, mask = true) => {
    if (!fullName || typeof fullName !== 'string') return '';
    const trimmed = fullName.trim();
    if (!mask) return trimmed;

    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

/**
 * Dispara la descarga en el navegador de un archivo blob CSV con codificación UTF-8.
 * @param {Blob|Uint8Array} blobData 
 * @param {string} filename 
 * @returns {boolean}
 */
export const downloadCsvBlob = (blobData, filename = 'participantes_grito_mexicano.csv') => {
    try {
        const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return true;
    } catch (err) {
        console.error('Error al descargar el archivo CSV:', err);
        return false;
    }
};

/**
 * Determina la categoría del grito según los decibeles pico.
 * @param {number} decibelsPeak 
 * @returns {string}
 */
export const determineScreamCategory = (decibelsPeak) => {
    const peak = Number(decibelsPeak) || 0;
    if (peak >= 110.0) return '¡Grito Legendario!';
    if (peak >= 95.0) return '¡Grito Patriota!';
    if (peak >= 80.0) return '¡Grito Bravo!';
    return '¡Grito con Orgullo!';
};

// =========================================================================
// WRAPPERS DE NEGOCIO (BL)
// =========================================================================

export const GetAdminConfigBL = async (roomId = null) => {
    return await DLGetAdminConfig(roomId);
};

export const UpdateAdminConfigBL = async (payload, roomId = null) => {
    // Validaciones previas del lado del cliente
    if (payload.durationSeconds && (payload.durationSeconds < 1 || payload.durationSeconds > 60)) {
        return { success: false, message: 'La duración del grito debe estar entre 1 y 60 segundos.' };
    }
    if (payload.ticketPrefix && payload.ticketPrefix.trim().length === 0) {
        return { success: false, message: 'El prefijo del ticket es requerido.' };
    }
    return await DLUpdateAdminConfig(payload, roomId);
};

export const GetAdminParticipantsBL = async (roomId = null, search = '', status = '', page = 1, pageSize = 50) => {
    return await DLGetAdminParticipants(roomId, search, status, page, pageSize);
};

export const ResetParticipantBL = async (payload) => {
    if (!payload.participantId) {
        return { success: false, message: 'Identificador de participante requerido.' };
    }
    if (!payload.reason || payload.reason.trim().length < 3) {
        return { success: false, message: 'Debes proporcionar un motivo válido para la reactivación (mínimo 3 caracteres).' };
    }
    return await DLResetParticipant(payload);
};

export const DisqualifyParticipantBL = async (payload) => {
    if (!payload.participantId) {
        return { success: false, message: 'Identificador de participante requerido.' };
    }
    return await DLDisqualifyParticipant(payload);
};

export const ExportParticipantsCsvBL = async (roomId = null, customFilename = null) => {
    const res = await DLExportParticipantsCsv(roomId);
    if (!res.success) {
        return res;
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    const finalFilename = customFilename || `participantes_grito_mexicano_${todayStr}.csv`;
    const downloaded = downloadCsvBlob(res.data, finalFilename);

    if (!downloaded) {
        return { success: false, message: 'No se pudo generar la descarga del archivo en el navegador.' };
    }

    return { success: true, message: 'Reporte CSV descargado exitosamente.' };
};

export const GetPublicConfigBL = async (roomId = null) => {
    return await DLGetPublicConfig(roomId);
};

export const RegisterParticipantBL = async (payload) => {
    if (!payload.fullName || payload.fullName.trim().length < 2) {
        return { success: false, message: 'El nombre completo debe tener al menos 2 caracteres.' };
    }
    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email.trim())) {
        return { success: false, message: 'El correo electrónico no tiene un formato válido.' };
    }
    if (!payload.phone || !/^\d{10}$/.test(payload.phone.trim())) {
        return { success: false, message: 'El teléfono debe contener exactamente 10 dígitos.' };
    }
    if (!payload.acceptedTerms) {
        return { success: false, message: 'Debes aceptar los Términos y Condiciones y Aviso de Privacidad.' };
    }
    return await DLRegisterParticipant(payload);
};

export const SubmitScoreBL = async (payload) => {
    if (!payload.participantId) {
        return { success: false, message: 'Identificador de participante no encontrado.' };
    }
    if (!payload.sessionToken) {
        return { success: false, message: 'Token de sesión no disponible.' };
    }
    return await DLSubmitScore(payload);
};

export const GetRankingBL = async (roomId = null, top = 20) => {
    return await DLGetRanking(roomId, top);
};

export const GetParticipantStatusBL = async (roomId, participantId) => {
    if (!participantId) {
        return { success: false, message: 'Identificador de participante no proporcionado.' };
    }
    return await DLGetParticipantStatus(roomId, participantId);
};

export const TrackMetricBL = async (roomId, eventName, sessionId, source = null) => {
    if (!eventName || typeof eventName !== 'string') {
        return { success: false, message: 'El nombre del evento es obligatorio.' };
    }
    const finalSessionId = sessionId || `anon_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const payload = {
        roomId,
        eventName: eventName.trim(),
        sessionId: finalSessionId,
        source: source ? source.trim() : null
    };
    return await DLTrackMetric(payload, roomId);
};

export const GetAdminStatsBL = async (roomId = null) => {
    return await DLGetAdminStats(roomId);
};

// =========================================================================
// SOPORTE ACÚSTICO WEB AUDIO API Y UTILIDADES MÓVILES (HU-05)
// =========================================================================

/**
 * Solicita el flujo de audio del micrófono con procesamiento nativo desactivado
 * para capturar la potencia acústica pura del grito del aficionado sin atenuaciones de llamada telefónica.
 * @returns {Promise<MediaStream>}
 */
export const requestMicrophoneStream = async () => {
    const constraints = {
        audio: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false
        }
    };
    return await navigator.mediaDevices.getUserMedia(constraints);
};

/**
 * Crea e inicializa el motor de audio Web Audio API con filtrado de paso de banda (300Hz-3400Hz)
 * optimizado para voz humana y cálculo en tiempo real de decibeles a 60 FPS.
 * @param {MediaStream} stream Flujo de audio del micrófono.
 * @returns {{ audioContext: AudioContext, analyser: AnalyserNode, filter: BiquadFilterNode, source: MediaStreamAudioSourceNode, getDecibels: Function, cleanup: Function }}
 */
export const createAudioEngine = (stream) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
        throw new Error('Web Audio API no está soportada en este dispositivo o navegador.');
    }

    const audioContext = new AudioContextClass();
    const source = audioContext.createMediaStreamSource(stream);

    // BiquadFilterNode tipo 'bandpass' para aislar voz humana (300Hz-3400Hz)
    // Con centro en 1500Hz y Q=1.0 para atenuar soplos mecánicos, golpes de viento y frecuencias ultra-altas
    const filter = audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1500;
    filter.Q.value = 1.0;

    // AnalyserNode de baja latencia para muestreo temporal continuo
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.3;

    // Conectar grafo de audio
    source.connect(filter);
    filter.connect(analyser);

    const bufferLength = analyser.fftSize;
    const dataArray = new Float32Array(bufferLength);

    /**
     * Obtiene los decibeles brutos medidos por el micrófono (sin Noise Gate).
     * Útil para calibración de ruido ambiente durante el conteo.
     * @returns {number} Decibeles acústicos brutos (0 - 120 dB)
     */
    const getRawDecibels = () => {
        analyser.getFloatTimeDomainData(dataArray);

        let sumSquares = 0;
        for (let i = 0; i < bufferLength; i++) {
            const sample = dataArray[i];
            sumSquares += sample * sample;
        }

        const rms = Math.sqrt(sumSquares / bufferLength);
        const dB_raw = 20 * Math.log10(Math.max(rms, 1e-5));
        return Math.min(120, Math.max(0, Math.round(((dB_raw + 100) / 100) * 120 * 10) / 10));
    };

    /**
     * Lee TimeDomainData Float32, calcula RMS y aplica Noise Gate / Calibración de Ruido de Fondo.
     * Si el sonido detectado está en o por debajo del piso de ruido ambiente, retorna 0.0 dB (0% en barra).
     * Cuando la voz supera el umbral, escala limpiamente de 0 a 120 dB.
     * @param {number} baselineNoise Nivel de ruido ambiental base muestreado durante el conteo (ej. 45-60 dB).
     * @returns {number} Decibeles calibrados en pantalla (0.0 - 120.0 dB)
     */
    const getDecibels = (baselineNoise = 0) => {
        const rawDb = getRawDecibels();

        // Umbral de compuerta acústica (Noise Gate):
        // El ruido de estática o ambiente de cualquier móvil en silencio suele rondar 42-55 dB
        const threshold = Math.max(42, (baselineNoise || 0) + 1.5);

        // Si el sonido está por debajo o dentro del ruido ambiente, la barra permanece estrictamente en 0.0
        if (rawDb <= threshold) {
            return 0;
        }

        // Escalado dinámico y suave desde 0 hasta 120 dB para el grito real
        const dynamicRange = 120 - threshold;
        if (dynamicRange <= 0) return rawDb;

        const scaled = ((rawDb - threshold) / dynamicRange) * 120;
        return Math.min(120, Math.max(0, Math.round(scaled * 10) / 10));
    };

    /**
     * Libera de forma segura las conexiones y cierra el AudioContext.
     */
    const cleanup = async () => {
        try {
            source.disconnect();
        } catch {
            // Ignorar
        }
        try {
            filter.disconnect();
        } catch {
            // Ignorar
        }
        try {
            analyser.disconnect();
        } catch {
            // Ignorar
        }
        try {
            if (audioContext && audioContext.state !== 'closed') {
                await audioContext.close();
            }
        } catch (e) {
            console.warn('Error al cerrar AudioContext en cleanup:', e);
        }
    };

    return {
        audioContext,
        analyser,
        filter,
        source,
        getDecibels,
        getRawDecibels,
        cleanup
    };
};

/**
 * Dispara una pulsación de vibración háptica si el hardware móvil lo soporta.
 * @param {number|number[]} pattern Patrón de vibración en milisegundos (ej. [100] o [100, 50, 100])
 */
export const triggerVibration = (pattern = [100]) => {
    try {
        if ('vibrate' in navigator && typeof navigator.vibrate === 'function') {
            navigator.vibrate(pattern);
        }
    } catch {
        // Dispositivo sin hardware de vibración o bloqueado por política de permisos
    }
};

/**
 * Solicita mantener encendida la pantalla del dispositivo móvil mediante Screen WakeLock API.
 * @returns {Promise<WakeLockSentinel|null>}
 */
export const requestWakeLock = async () => {
    try {
        if ('wakeLock' in navigator && typeof navigator.wakeLock.request === 'function') {
            const wakeLock = await navigator.wakeLock.request('screen');
            return wakeLock;
        }
    } catch (err) {
        console.warn('Screen WakeLock request failed:', err);
    }
    return null;
};

/**
 * Alterna el estado de activación de registros de la dinámica (abrir o pausar participación).
 * @param {boolean} isActive 
 * @param {string|null} roomId 
 * @returns {Promise<{success: boolean, message?: string, data?: any}>}
 */
export const ToggleActivePersonalNoisemeterBL = async (isActive, roomId = null) => {
    try {
        const response = await DLToggleActivePersonalNoisemeter(isActive, roomId);
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Error inesperado al alternar el estado de la dinámica.'
        };
    }
};

/**
 * Reinicia por completo la dinámica en la sala (elimina participantes, puntuaciones y telemetría de forma permanente).
 * @param {string|null} roomId 
 * @returns {Promise<{success: boolean, message?: string, data?: any}>}
 */
export const ResetPersonalNoisemeterDataBL = async (roomId = null) => {
    try {
        const response = await DLResetPersonalNoisemeterData(roomId);
        return response;
    } catch (error) {
        return {
            success: false,
            message: 'Error inesperado al reiniciar los datos de la dinámica.'
        };
    }
};
